import React from 'react';
import {
  LoaderFunctionArgs,
  Navigate,
  Outlet,
  RouteObject,
  matchPath,
  redirect,
  useLocation,
} from 'react-router';

import {useExactSelector} from '@edna/hooks';

import {agentPaymentsRoutes} from 'src/containers/AgentPayments/routes';
import {channelsRoutes} from 'src/containers/Channels/routes';
import {newsRoutes} from 'src/containers/CompanyNews/routes';
import {correctionsRoutes} from 'src/containers/Corrections/routes';
import {integrationRoutes} from 'src/containers/Integration/routes';
import {messageMatchersRoutes} from 'src/containers/MessageMatchers/routes';
import {notificationsHistoryRoutes} from 'src/containers/NotificationsHistory/routes';
import {ErrorBoundary} from 'src/containers/ServiceUnavailable/RouteErrorBoundary';
import {supportClientCardRoutes} from 'src/containers/SupportClients/SupportClientCard/routes';
import {supportRoutes} from 'src/containers/SupportTabs/routes';
import {ERoles, getCurrentUser, selectTenantId, useGetCurrentUserQuery} from 'src/containers/User';
import {userCompanyProfileRoutes} from 'src/containers/UserCompanyProfile/routes';
import PATHS, {AUTH_PATH_PREFIX, USER_AUTH_PATH_PREFIX} from 'src/paths';
import {getDispatch, getState} from 'src/routerContext';

import {Components, PRIVATE_ROUTES, PUBLIC_ROUTES, getPrivatePaths} from './routes';

const NESTED_ROUTES: Record<string, RouteObject> = {
  [PATHS.AGENT_PAYMENTS]: agentPaymentsRoutes,
  [PATHS.SUPPORT_AGENT_PAYMENTS]: agentPaymentsRoutes,
  [PATHS.NEWS]: newsRoutes,
  [PATHS.CORRECTIONS]: correctionsRoutes,
  [PATHS.COMPANY_PROFILE]: userCompanyProfileRoutes,
  [PATHS.CHANNELS]: channelsRoutes,
  [PATHS.NOTIFICATIONS]: notificationsHistoryRoutes,
  [PATHS.INTEGRATION]: integrationRoutes,
  [PATHS.MESSAGE_MATCHERS]: messageMatchersRoutes,
  [PATHS.SUPPORT]: supportRoutes,
  [PATHS.SUPPORT_CLIENT_CARD]: supportClientCardRoutes,
};

export const appPublicRoutes: RouteObject[] = Object.entries(PUBLIC_ROUTES).map(
  ([path, Component]) => ({
    path,
    Component,
    ...(NESTED_ROUTES[path] ?? {}),
  }),
);

export const appPrivateRoutes: RouteObject[] = Object.entries(PRIVATE_ROUTES).map(
  ([path, {component: Component}]) => ({
    path,
    Component,
    ...(NESTED_ROUTES[path] ?? {}),
  }),
);

const Home = () => {
  const {data: user, isLoading: isUserLoading} = useGetCurrentUserQuery();

  if (isUserLoading) {
    return null;
  }

  if (user?.roles?.includes(ERoles.ROLE_AGENT)) {
    return <Navigate to={PATHS.AGENT_PAYMENTS} />;
  }

  return <Components.Home />;
};

const PrivateLayout = () => {
  const {pathname} = useLocation();
  const tenantId = useExactSelector(selectTenantId);
  const {data: user, isLoading: isUserLoading} = useGetCurrentUserQuery();

  const resellerId = React.useMemo(() => {
    if (!user) {
      return null;
    }

    return user.tenants.find((tenant) => tenant.id === tenantId)?.resellerId;
  }, [user, tenantId]);

  const hasAccess = React.useMemo(() => {
    const allowedPrivatePaths =
      user && resellerId ? getPrivatePaths(user.permissions, user.roles, resellerId) : [];

    return allowedPrivatePaths.some((route) => matchPath(`${route}/*`, pathname));
  }, [user, resellerId, pathname]);

  if (isUserLoading) {
    return null;
  }

  return hasAccess ? <Outlet /> : <Components.NotFound />;
};

const privateMiddleware = async ({context}: LoaderFunctionArgs) => {
  const tenantId = getState(context).user?.tenantId;

  if (!tenantId) {
    throw redirect(PATHS.SIGN_IN);
  }

  const dispatch = getDispatch(context);
  const {data: user} = await dispatch(getCurrentUser.initiate());

  if (!user) {
    throw redirect(PATHS.SIGN_IN);
  }

  return null;
};

const publicMiddleware = async ({context}: LoaderFunctionArgs) => {
  const tenantId = getState(context).user?.tenantId;

  if (!tenantId) {
    return null;
  }

  const dispatch = getDispatch(context);
  const {data: user} = await dispatch(getCurrentUser.initiate());

  if (!user) {
    return null;
  }

  throw redirect(PATHS.HOME);
};

export const appRoutes: RouteObject[] = [
  {
    Component: PrivateLayout,
    middleware: [privateMiddleware],
    ErrorBoundary,
    children: [
      ...appPrivateRoutes,
      {
        index: true,
        Component: Home,
      },
      {
        path: `${AUTH_PATH_PREFIX}/*`,
        Component: Home,
      },
      {
        path: `${USER_AUTH_PATH_PREFIX}/*`,
        Component: Home,
      },
      {
        path: '*',
        Component: Components.NotFound,
      },
    ],
  },
  {
    element: <Outlet />,
    middleware: [publicMiddleware],
    children: [
      ...appPublicRoutes,
      {
        path: '*',
        element: <Navigate to={PATHS.SIGN_IN} />,
      },
    ],
  },
];
